import {
  Component,
  OnInit,
  ViewChild,
  Input,
  AfterViewInit,
  DestroyRef,
  inject,
  signal,
  effect,
  computed,
  ChangeDetectionStrategy,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';

import {
  ReactiveFormsModule,
  FormBuilder,
  FormGroup,
  Validators,
} from '@angular/forms';
import { MatTableModule, MatTableDataSource } from '@angular/material/table';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatSortModule, MatSort } from '@angular/material/sort';
import { MatCardModule } from '@angular/material/card';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { SecretsService } from '../../shared/services/secrets/secrets.service';
import { SecretsResourceStore } from '../../shared/services/secrets/secrets-resource.store';
import { DeploymentInfoService } from '../../shared/services/deployment-info/deployment-info.service';
import {
  SecretSource,
  CreateSecretSourceRequest,
  UpdateSecretSourceRequest,
  SecretSourceType,
  GcpSecretManagerConfig,
  SecretSourceConfig,
} from '../../shared/interfaces/secrets.interface';
import { SecretErrorHandlingUtils } from '../../shared/utils/secret-error-handling.utils';

enum SecretSourceFormMode {
  ADD = 'add',
  EDIT = 'edit',
}

/**
 * Secret source type constants
 */
const SECRET_SOURCE_TYPES = {
  DEFAULT: 'Default' as const,
  // deepcode ignore javascript/HardcodedNonCryptoSecret: server enum value identifying the secret-source type, not a secret
  VAULT: 'Vault' as const,
  // deepcode ignore javascript/HardcodedNonCryptoSecret: server enum value identifying the secret-source type, not a secret
  GCP_SECRET_MANAGER: 'GcpSecretManager' as const,
} as const;

/** Display labels for the source-type dropdown, keyed by the server enum value. */
const SOURCE_TYPE_LABELS: Record<string, string> = {
  Default: 'Default (Built-in)',
  // deepcode ignore javascript/HardcodedNonCryptoSecret: human-readable dropdown label, not a secret
  Vault: 'HashiCorp Vault',
  // deepcode ignore javascript/HardcodedNonCryptoSecret: human-readable dropdown label, not a secret
  GcpSecretManager: 'Google Cloud Secret Manager',
};

@Component({
  selector: 'app-secret-sources',
  standalone: true,
  imports: [
    ReactiveFormsModule,
    MatTableModule,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatSortModule,
    MatCardModule,
    MatProgressSpinnerModule,
    MatFormFieldModule,
    MatInputModule,
    MatSelectModule,
  ],
  templateUrl: './secret-sources.component.html',
  styleUrls: ['./secret-sources.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SecretSourcesComponent implements OnInit, AfterViewInit {
  private secretsService = inject(SecretsService);
  private readonly store = inject(SecretsResourceStore);
  private readonly deploymentInfo = inject(DeploymentInfoService);
  private readonly destroyRef = inject(DestroyRef);
  private fb = inject(FormBuilder);

  @Input() projectId: number | null = null;

  // Reads are backed by the shared SecretsResourceStore (Phase 0); the table
  // still uses MatTableDataSource so MatSort keeps working, kept in sync with
  // the store via an effect.
  dataSource = new MatTableDataSource<SecretSource>([]);
  displayedColumns: string[] = ['name', 'sourceType', 'actions'];
  readonly loading = this.store.isLoading;

  /** Surfaces failures from non-form actions (e.g. delete) at the top of the list. */
  actionError = signal<string | null>(null);

  // Inline form state
  showAddForm = signal(false);
  addForm: FormGroup;
  addFormLoading = signal(false);
  addFormError = signal<string | null>(null);

  // Edit form state
  showEditForm = signal(false);
  editForm: FormGroup;
  editFormLoading = signal(false);
  editFormError = signal<string | null>(null);
  editingSource = signal<SecretSource | null>(null);

  /**
   * Source-type options for the dropdown, driven by the deployment's usable
   * secret-source backends (SCRUM-783) rather than a hardcoded list — so
   * GcpSecretManager only appears on GCP deployments and Vault only where the
   * server has APP__VAULT__* configured (SCRUM-695). Falls back to Default only
   * if deployment-info hasn't loaded (or an older server didn't return
   * secretSources) so the form is never empty — Vault is never offered unless
   * the server explicitly lists it, since without server-side Vault config a
   * Vault source cannot resolve.
   */
  readonly sourceTypes = computed<{ value: SecretSourceType; label: string }[]>(() => {
    const available = this.deploymentInfo.secretSources();
    const types = available.length
      ? available
      : [SECRET_SOURCE_TYPES.DEFAULT];
    return types.map((t) => ({
      value: t as SecretSourceType,
      label: SOURCE_TYPE_LABELS[t] ?? t,
    }));
  });

  @ViewChild(MatSort) sort!: MatSort;

  // Expose constants to template
  readonly SECRET_SOURCE_TYPES = SECRET_SOURCE_TYPES;

  constructor() {
    this.addForm = this.createAddForm();
    this.editForm = this.createEditForm();
    // Keep the table data source in sync with the store's secret sources.
    effect(() => {
      this.dataSource.data = this.store.secretSources();
    });
  }

  ngOnInit(): void {
    this.store.setProjectId(this.projectId);
  }

  ngAfterViewInit(): void {
    this.dataSource.sort = this.sort;
  }

  /**
   * Universal form generator for SecretSource forms
   */
  private generateSecretSourceForm(mode: SecretSourceFormMode): FormGroup {
    return this.fb.group({
      name: ['', [Validators.required, Validators.minLength(2)]],
      // The source type cannot change once a source is created, so the edit
      // form's control is disabled at creation time via the FormControl config
      // rather than a template [disabled] binding (which triggers NG01051 on a
      // reactive control).
      sourceType: [
        {
          value: SECRET_SOURCE_TYPES.DEFAULT,
          disabled: mode === SecretSourceFormMode.EDIT,
        },
        [Validators.required],
      ],
      config: this.fb.group({
        // Vault sources need no config: the server reads the Vault url + token
        // from its own environment (SCRUM-695), so — like Default — the operator
        // supplies only the source name here and per-secret paths on the secrets.
        // GcpSecretManager: the GCP project short secret ids resolve against.
        projectId: [''],
      }),
    });
  }

  /**
   * Universal field validation for both forms
   */
  private validateFormField(form: FormGroup, fieldName: string): string | null {
    const field = form.get(fieldName);
    if (field && field.invalid && (field.dirty || field.touched)) {
      if (field.errors?.['required']) {
        return `${this.getFieldLabel(fieldName)} is required`;
      }
      if (field.errors?.['minlength']) {
        return `${this.getFieldLabel(fieldName)} must be at least ${field.errors['minlength'].requiredLength} characters`;
      }
      if (field.errors?.['invalidUrl']) {
        return `${this.getFieldLabel(fieldName)} must be a valid HTTP or HTTPS URL`;
      }
    }
    return null;
  }

  /**
   * Universal source type checking
   */
  private isVaultTypeForForm(form: FormGroup): boolean {
    return form.get('sourceType')?.value === SECRET_SOURCE_TYPES.VAULT;
  }

  private isDefaultTypeForForm(form: FormGroup): boolean {
    return form.get('sourceType')?.value === SECRET_SOURCE_TYPES.DEFAULT;
  }

  private isGcpTypeForForm(form: FormGroup): boolean {
    return (
      form.get('sourceType')?.value === SECRET_SOURCE_TYPES.GCP_SECRET_MANAGER
    );
  }

  /**
   * Universal source type change handler
   */
  private handleSourceTypeChange(form: FormGroup): void {
    const sourceType = form.get('sourceType')?.value;
    const configGroup = form.get('config') as FormGroup;
    const projectId = configGroup.get('projectId');

    // Reset the type-specific control, then enable + validate it only for the
    // source type that needs it. Disabling keeps unrelated fields out of both
    // validation and the form value. (Binding [disabled] on reactive controls in
    // the template would trigger NG01051.) Default and Vault need no config —
    // Vault's url + token come from the server's own environment (SCRUM-695).
    projectId?.clearValidators();
    projectId?.disable();

    if (sourceType === SECRET_SOURCE_TYPES.GCP_SECRET_MANAGER) {
      projectId?.enable();
      projectId?.setValidators([Validators.required]);
    }

    projectId?.updateValueAndValidity();
  }

  private createAddForm(): FormGroup {
    return this.generateSecretSourceForm(SecretSourceFormMode.ADD);
  }

  private createEditForm(): FormGroup {
    return this.generateSecretSourceForm(SecretSourceFormMode.EDIT);
  }

  onAddNew(): void {
    this.showAddForm.set(true);
    this.showEditForm.set(false); // Close edit form if open
    this.resetAddFormValues();
    this.onSourceTypeChange(); // Set initial validators
    this.addFormError.set(null);
  }

  onCancelAdd(): void {
    this.showAddForm.set(false);
    this.resetAddFormValues();
    this.addFormError.set(null);
  }

  onSaveAdd(): void {
    if (this.addForm.invalid || !this.projectId) {
      this.addForm.markAllAsTouched();
      return;
    }

    this.addFormLoading.set(true);
    this.addFormError.set(null);

    const formValue = this.addForm.value;
    const request: CreateSecretSourceRequest = {
      name: formValue.name,
      sourceType: formValue.sourceType,
      config: this.prepareConfig(formValue.sourceType, formValue.config),
    };

    this.secretsService
      .createSecretSource(this.projectId, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.addFormLoading.set(false);
          this.showAddForm.set(false);
          this.store.reload();
        },
        error: (error) => {
          this.addFormLoading.set(false);
          this.addFormError.set(
            SecretErrorHandlingUtils.getSecretErrorMessage(error),
          );
        },
      });
  }

  private prepareConfig(
    sourceType: SecretSourceType,
    config: any,
  ): SecretSourceConfig {
    // Default and Vault carry no config. Vault's url + token come from the
    // server's own environment (SCRUM-695), so nothing is sent here.
    if (
      sourceType === SECRET_SOURCE_TYPES.DEFAULT ||
      sourceType === SECRET_SOURCE_TYPES.VAULT
    ) {
      return {};
    }

    if (sourceType === SECRET_SOURCE_TYPES.GCP_SECRET_MANAGER) {
      // The server reads the snake_case `project_id` key from the config JSON.
      const gcpConfig: GcpSecretManagerConfig = {
        project_id: (config.projectId || '').trim(),
      };
      return gcpConfig;
    }

    return config || {};
  }

  // Template wrapper methods (arrow functions)
  getAddFormFieldError = (fieldName: string): string | null =>
    this.validateFormField(this.addForm, fieldName);

  private getFieldLabel(fieldName: string): string {
    const labels: { [key: string]: string } = {
      name: 'Source Name',
      sourceType: 'Source Type',
      'config.projectId': 'GCP Project ID',
    };
    return labels[fieldName] || fieldName;
  }

  // Template helper methods (getters for reactive binding)
  get isVaultType(): boolean {
    return this.isVaultTypeForForm(this.addForm);
  }

  get isDefaultType(): boolean {
    return this.isDefaultTypeForForm(this.addForm);
  }

  get isGcpType(): boolean {
    return this.isGcpTypeForForm(this.addForm);
  }

  get isEditVaultType(): boolean {
    return this.isVaultTypeForForm(this.editForm);
  }

  get isEditDefaultType(): boolean {
    return this.isDefaultTypeForForm(this.editForm);
  }

  get isEditGcpType(): boolean {
    return this.isGcpTypeForForm(this.editForm);
  }

  onSourceTypeChange(): void {
    this.handleSourceTypeChange(this.addForm);
  }

  onEditSourceTypeChange(): void {
    this.handleSourceTypeChange(this.editForm);
  }

  onEdit(secretSource: SecretSource): void {
    this.editingSource.set(secretSource);
    this.showEditForm.set(true);
    this.showAddForm.set(false); // Close add form if open
    this.editFormError.set(null);

    // Populate the form, mapping the stored config onto the form controls and
    // resetting stale fields. The GCP config key is snake_case (`project_id`)
    // while the form control is `projectId`, so map it explicitly — otherwise
    // editing a GCP source leaves Project ID blank and fails the required check.
    const cfg = (secretSource.config ?? {}) as Record<string, unknown>;
    this.editForm.patchValue({
      name: secretSource.name,
      sourceType: secretSource.sourceType,
      config: {
        projectId: (cfg['project_id'] as string) ?? '',
      },
    });

    this.onEditSourceTypeChange(); // Set validators based on source type
  }

  onCancelEdit(): void {
    this.showEditForm.set(false);
    this.editForm.reset();
    this.editFormError.set(null);
    this.editingSource.set(null);
  }

  onSaveEdit(): void {
    if (this.editForm.invalid || !this.projectId || !this.editingSource()) {
      this.editForm.markAllAsTouched();
      return;
    }

    this.editFormLoading.set(true);
    this.editFormError.set(null);

    const formValue = this.editForm.value;
    const sourceType = this.editingSource()!.sourceType;
    const request: UpdateSecretSourceRequest = {
      name: formValue.name,
      config: this.prepareConfig(sourceType, formValue.config),
    };

    this.secretsService
      .updateSecretSource(this.projectId, this.editingSource()!.sourceId, request)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.editFormLoading.set(false);
          this.showEditForm.set(false);
          this.editingSource.set(null);
          this.store.reload();
        },
        error: (error) => {
          this.editFormLoading.set(false);
          this.editFormError.set(
            SecretErrorHandlingUtils.getSecretErrorMessage(error),
          );
        },
      });
  }

  getEditFormFieldError = (fieldName: string): string | null =>
    this.validateFormField(this.editForm, fieldName);

  onDelete(secretSource: SecretSource): void {
    if (!this.projectId) return;

    this.actionError.set(null);
    this.secretsService
      .deleteSecretSource(this.projectId, secretSource.sourceId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: () => {
          this.store.reload();
        },
        error: (error) => {
          this.actionError.set(
            SecretErrorHandlingUtils.getSecretErrorMessage(error),
          );
        },
      });
  }

  /** Icon + short label for the source-type badge in the table. */
  sourceTypeDisplay(type: SecretSourceType): { icon: string; label: string } {
    switch (type) {
      case SECRET_SOURCE_TYPES.DEFAULT:
        return { icon: 'storage', label: 'Built-in' };
      case SECRET_SOURCE_TYPES.VAULT:
        return { icon: 'security', label: 'Vault' };
      case SECRET_SOURCE_TYPES.GCP_SECRET_MANAGER:
        return { icon: 'cloud', label: 'GCP Secret Manager' };
      default:
        return { icon: 'security', label: type };
    }
  }

  onActionMenu(secretSource: SecretSource, action: string): void {
    switch (action) {
      case 'edit':
        this.onEdit(secretSource);
        break;
      case 'delete':
        this.onDelete(secretSource);
        break;
    }
  }

  private closeAllForms(): void {
    this.showAddForm.set(false);
    this.showEditForm.set(false);
    this.addForm.reset();
    this.editForm.reset();
    this.addFormError.set(null);
    this.editFormError.set(null);
    this.editingSource.set(null);
  }

  private resetAddFormValues(): void {
    this.addForm.reset({
      name: '',
      sourceType: SECRET_SOURCE_TYPES.DEFAULT,
      config: {
        projectId: '',
      },
    });
  }
}
